import type {
  InjectionProtocol,
  ProtocolBox,
} from "@src/packages/injection-protocol/types";

/**
 * Which box she is holding, where her device and her route still leave two
 * walks.
 *
 * A narrowing axis between the route and `presentationDecision`. Mounjaro is a
 * four-dose KwikPen or a single-dose pen, Wegovy a FlexTouch or a single-dose
 * pen: both are "a pen" to the device question and go under the skin, and the
 * hands are different — one is primed and dialled, the other has a hidden
 * needle and nothing to set. A patient holding the single-dose pen was walked
 * through the KwikPen's prime and dial.
 *
 * Pure, like `deviceDecision` and `presentationDecision`, so the question is
 * tested press by press rather than read off a chain of ternaries.
 *
 * ### Session-only, for now
 *
 * Nothing her record holds today tells the two boxes apart: `medication.form`
 * is `pre_filled_pen` for both. The API's medication identity can name the
 * pack (`identity.presentation`), and reading it is #3821's work; when it
 * lands it becomes a `recorded` input here, offered for confirmation the way
 * the device is. Until then she is asked, and closing the sheet forgets the
 * answer, like the route.
 */

export type BoxDecisionStage =
  /** Ask which box she has; the procedures left disagree only on that. */
  | "ask"
  /** She says her box is none of these. Nothing to walk, and a reason to give. */
  | "unguided"
  /** Nothing to ask; `chosen` is what is left. */
  | "settled";

export type BoxDecisionInput = {
  /** The procedures her device and route answers left, market-filtered. */
  protocols: readonly InjectionProtocol[];
  /** The box id she picked this session. */
  answered: string | null;
  /** She has said her box is none of the ones offered. */
  unlisted: boolean;
};

export type BoxDecision = {
  stage: BoxDecisionStage;
  /** What the next axis narrows from. Empty while the question is open. */
  chosen: readonly InjectionProtocol[];
  /** The boxes offered, in catalogue order. Empty when nothing is asked. */
  options: readonly ProtocolBox[];
};

export function boxDecision({
  protocols,
  answered,
  unlisted,
}: BoxDecisionInput): BoxDecision {
  const boxes = protocols.flatMap((protocol) =>
    protocol.box ? [protocol.box] : [],
  );
  // Asked only where every procedure left names its box. Fewer than two, or a
  // procedure without one, means the box decides nothing: the contract test
  // holds that a box is declared exactly when a sibling needs telling apart.
  if (protocols.length < 2 || boxes.length !== protocols.length) {
    return { stage: "settled", chosen: protocols, options: [] };
  }
  if (unlisted) {
    return { stage: "unguided", chosen: [], options: boxes };
  }
  const picked = answered
    ? protocols.filter((protocol) => protocol.box?.id === answered)
    : [];
  // An answer that matches nothing — a box from another medication, left over
  // from an earlier open — is no answer, and she is asked.
  if (picked.length > 0) {
    return { stage: "settled", chosen: picked, options: boxes };
  }
  return { stage: "ask", chosen: [], options: boxes };
}
